import { SpreadSheetCell, SpreadSheetRow } from '@retail/products/types';
import {
  recognizedFinfoNrHeadings,
  recognizedMgItemNrHeadings,
  recognizedNobbNrHeadings,
} from '@retail/utils';

export type ItemIdentifierKey = 'mgItemNumber' | 'finfoNumber' | 'nobbNumber';

export type ItemIdentifiers = Partial<Record<ItemIdentifierKey, number>>;

export interface ItemIdentifiersMatch {
  found: ItemIdentifiers[];
  notFound: ItemIdentifiers[];
}

export interface ItemIdentifiersFilter {
  fileName: string;
  identifiers: ItemIdentifiers[];
}

const recognizedIdentifierHeadings: Record<ItemIdentifierKey, string[]> = {
  mgItemNumber: recognizedMgItemNrHeadings,
  finfoNumber: recognizedFinfoNrHeadings,
  nobbNumber: recognizedNobbNrHeadings,
};

export const itemIdentifierKeys = Object.keys(recognizedIdentifierHeadings) as ItemIdentifierKey[];

const normalizeHeader = (header: SpreadSheetCell | undefined) =>
  `${header ?? ''}`
    .replace(/^\uFEFF|[\u200B\u200C\u00A0]/g, '')
    .replace(/["'\s-]/g, '')
    .toLowerCase();

const toIdentifier = (cell: SpreadSheetCell | undefined) => {
  const value = Number(`${cell ?? ''}`.trim());
  return Number.isInteger(value) && value > 0 ? value : undefined;
};

const identifiersKey = (ids: ItemIdentifiers) =>
  itemIdentifierKeys.map((key) => ids[key] ?? '').join('|');

/** Returns undefined when no identifier column is recognized in the header row */
export const parseItemIdentifiersSpreadsheet = (
  rows: SpreadSheetRow[]
): ItemIdentifiers[] | undefined => {
  const [headerRow = [], ...dataRows] = rows;

  const columns = itemIdentifierKeys
    .map((key) => ({
      key,
      index: headerRow.findIndex((header) =>
        recognizedIdentifierHeadings[key].some((heading) =>
          normalizeHeader(header).includes(normalizeHeader(heading))
        )
      ),
    }))
    .filter(({ index }) => index !== -1);

  if (!columns.length) return undefined;

  const seen = new Set<string>();
  return dataRows
    .map((row) =>
      columns.reduce<ItemIdentifiers>((ids, { key, index }) => {
        const value = toIdentifier(row[index]);
        return value === undefined ? ids : { ...ids, [key]: value };
      }, {})
    )
    .filter((ids) => {
      const key = identifiersKey(ids);
      if (!Object.keys(ids).length || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const toIdentifierSets = (list: ItemIdentifiers[]) =>
  Object.fromEntries(
    itemIdentifierKeys.map((key) => [
      key,
      new Set(list.map((ids) => ids[key]).filter((id): id is number => id !== undefined)),
    ])
  ) as Record<ItemIdentifierKey, Set<number>>;

const hasMatch = (ids: ItemIdentifiers, sets: Record<ItemIdentifierKey, Set<number>>) =>
  itemIdentifierKeys.some((key) => {
    const id = ids[key];
    return id !== undefined && sets[key].has(id);
  });

export const matchItemIdentifiers = (
  identifiers: ItemIdentifiers[],
  items: ItemIdentifiers[]
): ItemIdentifiersMatch => {
  const itemSets = toIdentifierSets(items);
  return {
    found: identifiers.filter((ids) => hasMatch(ids, itemSets)),
    notFound: identifiers.filter((ids) => !hasMatch(ids, itemSets)),
  };
};

export const createItemIdentifiersPredicate = (identifiers: ItemIdentifiers[]) => {
  const identifierSets = toIdentifierSets(identifiers);
  return (item: ItemIdentifiers) => hasMatch(item, identifierSets);
};
